// Primitivo de reordenamiento por drag & drop, consistente en todo el admin
// (mismo @dnd-kit que el kanban). Reemplaza las flechas ↑/↓ dispersas.
//
// Uso:
//   <SortableList ids={items.map(getId)} onReorder={(from, to) =>
//        onChange(arrayMove(items, from, to))}>
//     {items.map((it) => (
//       <SortableItem key={getId(it)} id={getId(it)} className={styles.row}>
//         <DragHandle />
//         …contenido de la fila…
//       </SortableItem>
//     ))}
//   </SortableList>
//
// - El handle (GripVertical) es lo único que inicia el drag → el resto de la
//   fila sigue clickeable/editable (acordeones, inputs).
// - `disabled` desactiva el drag y oculta los handles (ej: hay un filtro
//   activo y el orden visible ≠ orden real).
import {
  createContext,
  useContext,
  type CSSProperties,
  type ReactNode,
} from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import styles from './SortableList.module.css';

export { arrayMove };

const ListContext = createContext<{ disabled: boolean }>({ disabled: false });
interface ItemCtx {
  attributes: Record<string, unknown>;
  listeners: Record<string, unknown> | undefined;
  isDragging: boolean;
  disabled: boolean;
}
const ItemContext = createContext<ItemCtx | null>(null);

interface SortableListProps {
  /** IDs en el orden visible actual. Estables por item. */
  ids: string[];
  /** Drag soltado: índices en el array `ids`. El consumer aplica arrayMove. */
  onReorder: (oldIndex: number, newIndex: number) => void;
  disabled?: boolean;
  /** 'list' = columna vertical (default). 'grid' = thumbnails que envuelven. */
  layout?: 'list' | 'grid';
  children: ReactNode;
}

export function SortableList({ ids, onReorder, disabled = false, layout = 'list', children }: SortableListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(e: DragEndEvent) {
    if (disabled) return;
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex >= 0 && newIndex >= 0) onReorder(oldIndex, newIndex);
  }

  return (
    <ListContext.Provider value={{ disabled }}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={ids}
          strategy={layout === 'grid' ? rectSortingStrategy : verticalListSortingStrategy}
        >
          {children}
        </SortableContext>
      </DndContext>
    </ListContext.Provider>
  );
}

interface SortableItemProps {
  id: string;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}

export function SortableItem({ id, className, style: extraStyle, children }: SortableItemProps) {
  const { disabled } = useContext(ListContext);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });
  const style: CSSProperties = {
    ...extraStyle,
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { opacity: 0.55, zIndex: 20, position: 'relative' } : null),
  };
  return (
    <ItemContext.Provider value={{ attributes, listeners, isDragging, disabled }}>
      <div ref={setNodeRef} className={className} style={style}>
        {children}
      </div>
    </ItemContext.Provider>
  );
}

interface DragHandleProps {
  className?: string;
  label?: string;
}

/** Agarradera de arrastre. Se oculta si la lista está `disabled`. */
export function DragHandle({ className, label = 'Arrastrar para reordenar' }: DragHandleProps) {
  const ctx = useContext(ItemContext);
  if (!ctx || ctx.disabled) return null;
  return (
    <button
      type="button"
      className={`${styles.handle}${className ? ` ${className}` : ''}`}
      aria-label={label}
      title={label}
      {...ctx.attributes}
      {...ctx.listeners}
    >
      <GripVertical size={15} aria-hidden />
    </button>
  );
}
